/**
 * Utility functions for the application
 */

/**
 * Generate a random string of specified length
 * @param length Length of the string to generate
 * @returns Random string
 */
export const randomString = (length: number): string => {
    const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';
    let result = '';
    for (let i = 0; i < length; i++) {
        result += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    return result;
};

/**
 * Wait for specified milliseconds
 * @param ms Milliseconds to wait
 * @returns Promise that resolves after the delay
 */
export const waitAsync = (ms: number): Promise<void> => {
    return new Promise(resolve => setTimeout(resolve, ms));
};

/**
 * Stringify value for SQL queries (web version just uses JSON.stringify)
 * @param value Value to stringify
 * @returns Stringified value
 */
export const stringify = (value: any): string => {
    return JSON.stringify(value);
};

/**
 * Check if a value is empty
 * @param value Value to check
 * @returns True if empty
 */
export const isEmpty = (value: any): boolean => {
    return value === null || value === undefined || value === '' || 
           (Array.isArray(value) && value.length === 0) ||
           (typeof value === 'object' && Object.keys(value).length === 0);
};

/**
 * Deep clone an object
 * @param obj Object to clone
 * @returns Cloned object
 */
export const deepClone = <T>(obj: T): T => {
    return JSON.parse(JSON.stringify(obj));
};

/**
 * Format date to string
 * @param date Date to format
 * @returns Formatted date string
 */
export const formatDate = (date: Date): string => {
    return date.toISOString().split('T')[0];
};

/**
 * Debounce function
 * @param func Function to debounce
 * @param wait Wait time in milliseconds
 * @returns Debounced function
 */
export const debounce = <T extends (...args: any[]) => any>(
    func: T,
    wait: number
): ((...args: Parameters<T>) => void) => {
    let timeout: NodeJS.Timeout;
    return (...args: Parameters<T>) => {
        clearTimeout(timeout);
        timeout = setTimeout(() => func(...args), wait);
    };
};